/**
 * composeItemName — shared composer used by the ItemEntry preview (P5),
 * the ItemEntry Save path, and the Items list secondary display (P8).
 *
 * Purely a projection of `item_data` values → a display string. Never
 * mutates state and never talks to the backend. Rules match the CDI
 * industry-standard naming order:
 *
 *   Wine    → Producer + Region (optional) + Grape(s) + Vintage + Bottle Size (naming/inline)
 *             Producer · Region · Grape(s) · Vintage (identity dots, no Bottle Size)
 *   Whisky  → Distillery + Whisky Type + Age + Cask Type + Region + ABV + Bottle Size (naming/inline)
 *             Distillery · Whisky Type · Age · Cask Type · Region · ABV (identity dots, no Bottle Size)
 *   Spirits → Brand + Spirit Type + Age (optional) + Cask Type / Finish + Region (optional) + ABV + Bottle Size (naming/inline)
 *             Brand · Spirit Type · Age · Cask Type / Finish · Region · ABV (identity dots, no Bottle Size)
 *   Other   → "" (caller falls back to raw Item Name)
 *
 * All three presets are locked to their global-industry naming spec.
 * Supporting fields (Country, Distillery / Producer, Style, notes,
 * batch, bottling year, etc.) are deliberately excluded from every
 * naming surface.
 *
 * Optional fields (Appellation on Wine) are automatically skipped when
 * blank via the `filter(Boolean)` in joinName — no separate branch
 * needed. Legacy field-name fallbacks preserve pre-rename data.
 *
 * Type detection reproduces the ItemEntry heuristics so behaviour stays
 * in lock-step: prefer explicit `collection.type_id`, fall back to a
 * name-regex on the collection label for legacy collections that never
 * had `type_id` set.
 */

const joinName = (parts) =>
  parts
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

/** collectionType(collection) → "wine" | "whisky" | "spirits" | "other" */
export function collectionType(collection) {
  const tid = String(collection?.type_id || "").toLowerCase().trim();
  if (tid === "wine") return "wine";
  if (tid === "whisky" || tid === "whiskey") return "whisky";
  if (tid === "spirits") return "spirits";
  const name = String(collection?.name || "");
  if (!tid) {
    if (/wine/i.test(name)) return "wine";
    if (/(whisky|whiskey)/i.test(name)) return "whisky";
    if (/(rum|tequila|cognac|gin|vodka|mezcal|armagnac|brandy|liqueur|spirits?)/i.test(name)) return "spirits";
  }
  return "other";
}

/**
 * composeAutoName(itemData, collection) → string
 *   Returns the composed identity-based name for the collection's type,
 *   or "" when the collection is a non-composing type (falls back to raw
 *   Item Name at the caller). Empty per-field pieces are skipped.
 */
export function composeAutoName(itemData, collection) {
  const src = itemData || {};
  switch (collectionType(collection)) {
    case "wine":
      // Wine Collector Naming (2026-02-26 revision):
      //   Producer + [Appellation OR Region fallback] + Cuvée
      //   + [Grape(s) when "Include Grape in Name?" is not false]
      //   + Vintage + ABV + Bottle Size
      // Appellation takes precedence — Region is only used when
      // Appellation is blank. Country and Classification are
      // deliberately EXCLUDED from the join list — they persist as
      // metadata for analytics only. Grape(s) is user-gated via the
      // "Include Grape in Name?" checkbox (defaults ON per the
      // `!== false` guard). Old World example: entering "Barolo"
      // in Appellation automatically suppresses "Piedmont" (Region).
      // New World example: entering "Pinot Noir" in Appellation on
      // a "Taylors" Producer emits "Taylors Pinot Noir" and hides the
      // Region (e.g. Hawke's Bay) from the name string. Blank slots
      // skip via filter(Boolean).
      {
        const origin = src["Appellation"] ? src["Appellation"] : src["Region"];
        const includeGrape = src["Include Grape in Name?"] !== false;
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinName([
          src["Producer"],
          origin,
          src["Cuvée"],
          includeGrape ? src["Grape(s)"] : "",
          src["Vintage"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    case "whisky":
      // Whisky Collector Naming (Phase 5, 2026-02):
      //   Distillery + Whisky Type + Age / Classification + ABV + Bottle Size
      // ABV renders as "<num>%" (no "ABV" suffix). Legacy Age /
      // Cask Type / Region stay in metadata and no longer drive the
      // naming engine on this track. Item Name (aka Expression
      // override) is applied at the caller — this composer returns
      // the auto string only.
      {
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinName([
          src["Distillery"],
          src["Whisky Type"],
          src["Age / Classification"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    case "spirits":
      // Spirits Collector Naming (Phase 2, 2026-02):
      //   Brand + Spirit Type + Age / Classification + ABV + Bottle Size
      // ABV renders as "<num>%" (no "ABV" suffix) so the composed name
      // reads e.g. "Herradura Tequila Añejo 40% 750ml". Blank fields
      // auto-skip via joinName's filter(Boolean). Legacy Age /
      // Cask Type / Finish / Region live on as metadata rows and are
      // NOT part of the naming engine on this track. Item Name (aka
      // Expression override) is applied at the caller — this composer
      // returns the auto string only.
      {
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinName([
          src["Brand"],
          src["Spirit Type"],
          src["Age Classification"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    default:
      return "";
  }
}

/** isNameOverride(itemData) — mirrors ItemEntry's "Name Override" flag. */
export function isNameOverride(itemData) {
  return !!(itemData || {})["Name Override"];
}

/**
 * composeIdentityDots(itemData, collection) → string
 *   Dot-separated identity variant used by the Items list secondary
 *   line and the Home Continue pill secondary line (both surfaces
 *   requested `Producer · Appellation · Vintage` style for wine, etc.).
 *   Bottle Size / ABV are included in the naming order per CDI
 *   industry-standard spec.
 *
 *   Wine    → Producer · Appellation · Grape · Vintage · Bottle Size
 *   Whisky  → Distillery · Whisky Type · Age · Region · ABV
 *   Spirits → Producer · Spirit Type · Expression · Region · ABV
 *   Other   → ""  (caller decides how to fall back)
 */
export function composeIdentityDots(itemData, collection) {
  const src = itemData || {};
  const joinDots = (parts) =>
    parts
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
      .join(" · ");
  switch (collectionType(collection)) {
    case "wine":
      // Wine Collector Identity Dots (Phase 7, 2026-02):
      //   Producer · [Appellation OR Region] · Grape(s) · Vintage · ABV · Bottle Size
      // Grape(s) is user-gated by the "Include Grape in Name?" toggle
      // (defaults ON via `!== false`) so hiding it from the main name
      // also hides it from the secondary identity line. ABV renders
      // as "<num>%" — aligned with Whisky / Spirits so every naming
      // surface uses the same suffix rule (2026-02 auto-name
      // consistency fix). Bottle Size is the trailing token per the
      // CDI global-industry naming spec. Blank slots skip via
      // filter(Boolean).
      {
        const appellation = String(src["Appellation"] ?? "").trim();
        const region = String(src["Region"] ?? "").trim();
        const wineOrigin = appellation || region;
        const includeGrape = src["Include Grape in Name?"] !== false;
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinDots([
          src["Producer"],
          wineOrigin,
          includeGrape ? src["Grape(s)"] : "",
          src["Vintage"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    case "whisky":
      // Whisky Identity Dots (Phase 5, 2026-02):
      //   Distillery · Whisky Type · Age / Classification · ABV · Bottle Size
      // ABV renders as "<num>%" (no "ABV" suffix). Blank fields skip.
      {
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinDots([
          src["Distillery"],
          src["Whisky Type"],
          src["Age / Classification"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    case "spirits":
      // Spirits Identity Dots (Phase 2, 2026-02):
      //   Brand · Spirit Type · Age / Classification · ABV · Bottle Size
      // ABV renders as "<num>%" (no "ABV" suffix). Blank fields skip.
      {
        const rawAbv = String(src["ABV"] ?? "").trim();
        const abvPct = rawAbv ? (rawAbv.includes("%") ? rawAbv : `${rawAbv}%`) : "";
        return joinDots([
          src["Brand"],
          src["Spirit Type"],
          src["Age Classification"],
          abvPct,
          src["Bottle Size"],
        ]);
      }
    default:
      return "";
  }
}
